/**
 * Web Vitals instrumentation — captures LCP, INP, CLS, TTFB and FCP
 * per route.
 *
 * Dev: logs colorized metrics to the console.
 * Prod: fire-and-forget insert into `public.web_vitals_events`. The
 *       INSERT policy is scoped to anon/authenticated with strict
 *       WITH CHECK constraints (metric_name enum, value range, route
 *       length). SELECT is admin-only, so metrics are safe to write
 *       from any client. The RPC `get_web_vitals_route_summary`
 *       powers the internal dashboard consumer.
 */
import type { Metric } from "web-vitals";

const ALLOWED_METRICS = new Set(["LCP", "FCP", "INP", "CLS", "TTFB", "FID"]);
const MAX_VALUE_MS = 600_000;

function getRoute(): string {
  const path = window.location.pathname || "/";
  // Policy limits route length to 200 chars.
  return path.length > 200 ? path.slice(0, 200) : path;
}

function truncateUserAgent(): string | null {
  const ua = navigator.userAgent || "";
  if (!ua) return null;
  return ua.length > 500 ? ua.slice(0, 500) : ua;
}

async function persistMetric(payload: {
  metric_name: string;
  metric_value_ms: number;
  rating: string | null;
  route: string;
  navigation_type: string | null;
  user_agent: string | null;
}) {
  try {
    // Lazy import to keep web-vitals fully off the initial path — the
    // Supabase client is already in the entry graph, but we avoid an
    // eager import to keep this module tree-shakeable in tests.
    const { supabase } = await import("@/integrations/supabase/client");
    // Fire-and-forget. Errors (RLS, offline, rate-limited) are swallowed
    // silently — losing a metric is strictly preferable to disrupting UX.
    void supabase.from("web_vitals_events").insert(payload);
  } catch {
    // Ignore — analytics best-effort.
  }
}

function reportMetric(metric: Metric) {
  const name = metric.name;
  if (!ALLOWED_METRICS.has(name)) return;

  // CLS is unitless (0..1). The DB column stores milliseconds, so we
  // store CLS × 1000 (same convention already used in dev logs and in
  // `get_web_vitals_route_summary` consumers).
  const rawValue = name === "CLS" ? metric.value * 1000 : metric.value;
  const value = Math.round(rawValue);
  if (!Number.isFinite(value) || value < 0 || value > MAX_VALUE_MS) return;

  const rating =
    metric.rating === "good" || metric.rating === "needs-improvement" || metric.rating === "poor"
      ? metric.rating
      : null;

  const route = getRoute();

  if (import.meta.env.DEV) {
    const color =
      rating === "good"
        ? "color: #0cce6b"
        : rating === "needs-improvement"
          ? "color: #ffa400"
          : "color: #ff4e42";
    console.debug(
      `%c[Web Vital] ${name}: ${value}${name === "CLS" ? " (x1000)" : "ms"} [${rating ?? "n/a"}] — ${route}`,
      color,
    );
    return;
  }

  void persistMetric({
    metric_name: name,
    metric_value_ms: value,
    rating,
    route,
    navigation_type: metric.navigationType ?? null,
    user_agent: truncateUserAgent(),
  });
}

export async function initWebVitals() {
  try {
    const { onLCP, onINP, onCLS, onTTFB, onFCP } = await import("web-vitals");
    onLCP(reportMetric);
    onINP(reportMetric);
    onCLS(reportMetric);
    onTTFB(reportMetric);
    onFCP(reportMetric);
  } catch {
    // web-vitals not available — silently ignore
  }
}
